import React from 'react';
import { Star, X } from 'lucide-react';
import { Eyebrow } from './ui/Eyebrow';
import { Button } from './ui/Button';
import type { WishlistEntry } from '../types/social';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface Props {
  open: boolean;
  current: WishlistEntry[];
  pendingCode: string | null;
  onPick: (codeToRemove: string) => void;
  onClose: () => void;
}

export function PriorityLimitModal({ open, current, pendingCode, onPick, onClose }: Props) {
  useBodyScrollLock(open);
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Limite de prioridades atingido"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-lg p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] my-auto max-h-[90vh] overflow-y-auto animate-fade-up relative"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="absolute top-3 right-3 p-2 rounded-full text-ink-500 hover:text-ink-900 hover:bg-ink-100"
        >
          <X size={18} />
        </button>
        <div className="flex items-center gap-2 mb-2">
          <span className="w-8 h-8 rounded-full bg-gold-100 flex items-center justify-center">
            <Star size={16} className="text-gold-600" fill="currentColor" />
          </span>
          <Eyebrow color="#C9A227">Limite atingido</Eyebrow>
        </div>
        <h2 className="font-display font-bold text-h2 text-ink-900 mb-1">
          Você já tem 5 prioridades
        </h2>
        <p className="font-body text-small text-ink-500 mb-4">
          {pendingCode
            ? `Pra adicionar ${pendingCode}, escolhe qual sai da lista.`
            : 'Escolhe qual sai da lista.'}
        </p>

        <ul className="space-y-2 mb-4">
          {current.map((w) => (
            <li
              key={w.sticker_code}
              className="flex items-center justify-between gap-3 bg-paper border border-ink-300 rounded-lg px-3 py-2.5"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-7 h-7 rounded-full bg-green-700 text-white font-display font-bold flex items-center justify-center text-[12px] shrink-0">
                  {w.priority}
                </span>
                <span className="font-mono font-semibold text-ink-900 truncate">
                  {w.sticker_code}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onPick(w.sticker_code)}
                className="text-caption font-bold text-danger hover:text-ruby-700 transition-colors px-2 py-1 rounded-md hover:bg-ruby-100 btn-focus"
              >
                Remover
              </button>
            </li>
          ))}
        </ul>

        <Button variant="ghost" size="md" fullWidth onClick={onClose}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
